/**
 * CaseTrackUS - Professional Dashboard
 * Simple structure:
 * - Individual clients: client → cases + family members with cases
 * - Company clients: company → workers → cases (DOL + USCIS only)
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, Tabs, Tab,
  Card, CardContent, IconButton, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Divider, Menu, MenuItem,
  Autocomplete, FormControl, InputLabel, Select
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axios from 'axios';
import { NATIONALITY_OPTIONS } from '../constants/nationalityCodes';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import { checkCase, getCaseHistory, deleteCase } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'https://casetrackus.com';
const api = axios.create({ baseURL: API_URL });

export default function ProfessionalDashboard({ user, onLogout }) {
  const token = localStorage.getItem('token');
  const [tab, setTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Settings
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsName, setSettingsName] = useState(user?.full_name || '');
  const [settingsEmailIndiv, setSettingsEmailIndiv] = useState(user?.default_emails_individual || '');
  const [settingsEmailComp, setSettingsEmailComp] = useState(user?.default_emails_company || '');
  const [settingsMsg, setSettingsMsg] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);

  // Add client
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientNotifEmails, setNewClientNotifEmails] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const currentType = tab === 0 ? 'individual' : 'company';
  const filteredClients = clients.filter(c => c.client_type === currentType);

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/pro/clients?token=${token}`);
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch(e) { setError('Failed to load clients'); }
    finally { setLoading(false); }
  };

  const handleAddClient = async () => {
    if (!newClientName.trim()) return;
    setAddLoading(true);
    try {
      const defaultEmails = currentType === 'individual'
        ? user?.default_emails_individual : user?.default_emails_company;
      await api.post(`/pro/clients?token=${token}`, {
        client_type: currentType,
        name: newClientName,
        client_email: newClientEmail || null,
        notification_emails: newClientNotifEmails || defaultEmails || null,
      });
      setAddClientOpen(false);
      setNewClientName(''); setNewClientEmail(''); setNewClientNotifEmails('');
      loadClients();
    } catch(e) { setError('Failed to add client'); }
    finally { setAddLoading(false); }
  };

  const [deleteClientId, setDeleteClientId] = useState(null);
  const [deleteClientName, setDeleteClientName] = useState('');

  const handleDeleteClient = async () => {
    if (!deleteClientId) return;
    try {
      await api.delete(`/pro/clients/${deleteClientId}?token=${token}`);
      setDeleteClientId(null);
      setDeleteClientName('');
      loadClients();
    } catch(e) { setError('Failed to delete client: ' + (e.response?.data?.detail || e.message)); }
  };

  const confirmDeleteClient = (clientId, clientName) => {
    setDeleteClientId(clientId);
    setDeleteClientName(clientName);
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    setSettingsMsg('');
    try {
      await api.put(`/auth/me?token=${token}`, {
        full_name: settingsName,
        default_emails_individual: settingsEmailIndiv,
        default_emails_company: settingsEmailComp
      });
      setSettingsMsg('✅ Settings saved!');
    } catch(e) { setSettingsMsg('❌ Failed to save'); }
    finally { setSettingsLoading(false); }
  };

  const openAddClient = () => {
    const defaultEmails = currentType === 'individual'
      ? user?.default_emails_individual || ''
      : user?.default_emails_company || '';
    setNewClientNotifEmails(defaultEmails);
    setAddClientOpen(true);
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#F5F5F5' }}>
      {/* Header */}
      <Box sx={{ background: '#1B5E20', color: 'white', py: 1.5, px: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>🗽 CaseTrackUS</Typography>
            <Chip label="Professional" size="small" sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.7rem' }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.85rem', opacity: 0.85 }}>{user?.full_name || user?.email}</Typography>
            <IconButton color="inherit" size="small" onClick={e => setMenuAnchor(e.currentTarget)}>
              <SettingsIcon fontSize="small" />
            </IconButton>
            <IconButton color="inherit" size="small" onClick={onLogout}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); setSettingsOpen(true); }}>⚙️ Account Settings</MenuItem>
      </Menu>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}
            sx={{ '& .MuiTab-root.Mui-selected': { color: '#1B5E20' }, '& .MuiTabs-indicator': { background: '#1B5E20' } }}>
            <Tab label={`👤 Individual (${clients.filter(c => c.client_type === 'individual').length})`} />
            <Tab label={`🏢 Company (${clients.filter(c => c.client_type === 'company').length})`} />
          </Tabs>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddClient}
            sx={{ background: '#1B5E20', '&:hover': { background: '#2E7D32' } }}>
            Add {currentType === 'individual' ? 'Individual Client' : 'Company'}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress sx={{ color: '#1B5E20' }} /></Box>
        ) : filteredClients.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, background: 'white', borderRadius: 2 }}>
            <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>{currentType === 'individual' ? '👤' : '🏢'}</Typography>
            <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
              No {currentType === 'individual' ? 'individual clients' : 'companies'} yet
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddClient}
              sx={{ background: '#1B5E20' }}>
              Add {currentType === 'individual' ? 'Individual Client' : 'Company'}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredClients.map(client => (
              <ClientCard key={client.id} client={client} token={token}
                onDelete={() => confirmDeleteClient(client.id, client.name)}
                onRefresh={loadClients} user={user} />
            ))}
          </Box>
        )}
      </Container>

      {/* Delete Client Confirmation Dialog */}
      <Dialog open={!!deleteClientId} onClose={() => setDeleteClientId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#C62828', fontWeight: 'bold' }}>⚠️ Delete Client</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            You are about to permanently delete <strong>{deleteClientName}</strong> and all associated data:
          </Alert>
          <Typography sx={{ fontSize: '0.9rem', color: '#555' }}>
            • All cases and case history<br/>
            • All family members and their cases<br/>
            • All matters and beneficiaries
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#C62828', mt: 2, fontWeight: 600 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteClientId(null)}>Cancel</Button>
          <Button onClick={handleDeleteClient} variant="contained" color="error">
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog open={addClientOpen} onClose={() => setAddClientOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {currentType === 'individual' ? '👤 Add Individual Client' : '🏢 Add Company'}
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth
            label={currentType === 'individual' ? 'Client Full Name' : 'Company Name'}
            value={newClientName} onChange={e => setNewClientName(e.target.value)}
            sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Client Email (optional)"
            value={newClientEmail} onChange={e => setNewClientEmail(e.target.value)}
            sx={{ mb: 2 }} />
          <TextField fullWidth label="Notification Emails (comma-separated)"
            value={newClientNotifEmails} onChange={e => setNewClientNotifEmails(e.target.value)}
            helperText="Emails to notify on status changes" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddClientOpen(false)}>Cancel</Button>
          <Button onClick={handleAddClient} variant="contained" disabled={addLoading || !newClientName.trim()}
            sx={{ background: '#1B5E20' }}>
            {addLoading ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => { setSettingsOpen(false); setSettingsMsg(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>⚙️ Account Settings</DialogTitle>
        <DialogContent>
          {settingsMsg && <Alert severity={settingsMsg.startsWith('✅') ? 'success' : 'error'} sx={{ mb: 2 }}>{settingsMsg}</Alert>}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>PROFILE</Typography>
          <TextField fullWidth label="Firm / Full Name" value={settingsName}
            onChange={e => setSettingsName(e.target.value)} sx={{ mb: 3 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>DEFAULT NOTIFICATION EMAILS</Typography>
          <TextField fullWidth label="For Individual Clients"
            value={settingsEmailIndiv} onChange={e => setSettingsEmailIndiv(e.target.value)}
            sx={{ mb: 2 }} helperText="Comma-separated — applied by default to new Individual clients" />
          <TextField fullWidth label="For Company Clients"
            value={settingsEmailComp} onChange={e => setSettingsEmailComp(e.target.value)}
            helperText="Comma-separated — applied by default to new Company clients" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSettingsOpen(false); setSettingsMsg(''); }}>Cancel</Button>
          <Button onClick={handleSaveSettings} variant="contained" disabled={settingsLoading}
            sx={{ background: '#1B5E20' }}>
            {settingsLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


// ─────────────────────────────────────
// Client Card
// ─────────────────────────────────────
function ClientCard({ client, token, onDelete, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [workers, setWorkers] = useState([]);  // for company
  const [cases, setCases] = useState([]);       // for individual
  const [loading, setLoading] = useState(false);

  // Add worker dialog (company)
  const [addWorkerOpen, setAddWorkerOpen] = useState(false);
  const [workerName, setWorkerName] = useState('');
  const [workerMatterNum, setWorkerMatterNum] = useState('');
  const [workerLoading, setWorkerLoading] = useState(false);

  // Family members (individual)
  const [members, setMembers] = useState([]);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [memberName, setMemberName] = useState('');
  const [memberRelation, setMemberRelation] = useState('Spouse');
  const [memberLoading, setMemberLoading] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState('');

  // Add case dialog (individual)
  const [addCaseOpen, setAddCaseOpen] = useState(false);
  const [caseType, setCaseType] = useState('eoir');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [alienNumber, setAlienNumber] = useState('');
  const [selectedNat, setSelectedNat] = useState(null);
  const [caseDesc, setCaseDesc] = useState('');
  const [caseLoading, setCaseLoading] = useState(false);
  const [caseError, setCaseError] = useState('');

  useEffect(() => {
    if (expanded) load();
  }, [expanded]);

  const load = async () => {
    setLoading(true);
    try {
      if (client.client_type === 'company') {
        const res = await axios.get(`${API_URL}/pro/clients/${client.id}?token=${token}`);
        const allMatters = res.data.matters || [];
        const allWorkers = [];
        for (const m of allMatters) {
          for (const b of (m.beneficiaries || [])) {
            allWorkers.push({ ...b, matter_id: m.id, matter_number: m.matter_number });
          }
        }
        setWorkers(allWorkers);
      } else {
        // Get cases and family members for individual client
        const [clientRes, membersRes] = await Promise.all([
          axios.get(`${API_URL}/pro/clients/${client.id}?token=${token}`),
          axios.get(`${API_URL}/pro/clients/${client.id}/members?token=${token}`)
        ]);
        const allMatters = clientRes.data.matters || [];
        const allCases = [];
        for (const m of allMatters) {
          const casesRes = await axios.get(`${API_URL}/pro/matters/${m.id}/cases?token=${token}`);
          allCases.push(...(casesRes.data || []).map(c => ({ ...c, matter_number: m.matter_number })));
        }
        setCases(allCases);
        setMembers(Array.isArray(membersRes.data) ? membersRes.data : []);
      }
    } catch(e) {}
    finally { setLoading(false); }
  };

  const handleAddWorker = async () => {
    if (!workerName.trim()) return;
    setWorkerLoading(true);
    try {
      // Create matter first
      const matterRes = await axios.post(`${API_URL}/pro/clients/${client.id}/matters?token=${token}`, {
        matter_number: workerMatterNum || null,
        description: null
      });
      // Add beneficiary to matter
      await axios.post(`${API_URL}/pro/matters/${matterRes.data.id}/beneficiaries?token=${token}`, {
        full_name: workerName
      });
      setAddWorkerOpen(false);
      setWorkerName(''); setWorkerMatterNum('');
      load();
    } catch(e) {}
    finally { setWorkerLoading(false); }
  };

  const handleAddMember = async () => {
    if (!memberName.trim()) return;
    setMemberLoading(true);
    try {
      await axios.post(`${API_URL}/pro/clients/${client.id}/members?token=${token}&full_name=${encodeURIComponent(memberName)}&relationship_type=${encodeURIComponent(memberRelation)}`);
      setAddMemberOpen(false);
      setMemberName(''); setMemberRelation('Spouse');
      load();
    } catch(e) {}
    finally { setMemberLoading(false); }
  };

  const handleAddCase = async () => {
    setCaseError('');
    if (caseType === 'uscis' && !receiptNumber.trim()) { setCaseError('Receipt number required'); return; }
    if (caseType === 'eoir' && (!alienNumber.trim() || !selectedNat)) { setCaseError('A-Number and nationality required'); return; }
    setCaseLoading(true);
    try {
      // Get or create first matter for individual
      let matterId;
      const clientRes = await axios.get(`${API_URL}/pro/clients/${client.id}?token=${token}`);
      if (clientRes.data.matters?.length > 0) {
        matterId = clientRes.data.matters[0].id;
      } else {
        const mRes = await axios.post(`${API_URL}/pro/clients/${client.id}/matters?token=${token}`, {});
        matterId = mRes.data.id;
      }
      await axios.post(`${API_URL}/cases?token=${token}`, {
        case_type: caseType,
        description: caseDesc || null,
        pro_matter_id: matterId,
        family_member_id: selectedMemberId ? parseInt(selectedMemberId) : null,
        ...(caseType === 'uscis' ? { receipt_number: receiptNumber.trim().toUpperCase() } : {
          eoir_alien_number: alienNumber.replace(/\D/g, ''),
          eoir_nationality_code: selectedNat?.code,
          eoir_nationality_label: selectedNat?.label,
        })
      });
      setAddCaseOpen(false);
      setReceiptNumber(''); setAlienNumber(''); setSelectedNat(null); setCaseDesc('');
      load();
    } catch(e) {
      setCaseError(e.response?.data?.detail || 'Failed to add case');
    } finally { setCaseLoading(false); }
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', flex: 1 }}
            onClick={() => setExpanded(!expanded)}>
            <Typography sx={{ fontSize: '1.5rem' }}>{client.client_type === 'individual' ? '👤' : '🏢'}</Typography>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{client.name}</Typography>
              {client.client_email && (
                <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>{client.client_email}</Typography>
              )}
              {client.notification_emails && (
                <Typography sx={{ fontSize: '0.72rem', color: '#1565C0' }}>
                  📧 {client.notification_emails}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {expanded && client.client_type === 'company' && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => setAddWorkerOpen(true)}
                sx={{ color: '#1B5E20', fontSize: '0.75rem', mr: 1 }}>
                Add Worker
              </Button>
            )}
            {expanded && client.client_type === 'individual' && (
              <>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAddMemberOpen(true)}
                  sx={{ color: '#666', fontSize: '0.75rem', mr: 0.5 }}>
                  Add Family Member
                </Button>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAddCaseOpen(true)}
                  sx={{ color: '#1B5E20', fontSize: '0.75rem', mr: 1 }}>
                  Add Case
                </Button>
              </>
            )}
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <IconButton size="small" onClick={onDelete} sx={{ color: '#C62828' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Expanded content */}
        {expanded && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} sx={{ color: '#1B5E20' }} /></Box>
            ) : client.client_type === 'company' ? (
              /* Company — show workers */
              workers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography sx={{ color: '#999', fontSize: '0.85rem', mb: 1 }}>No workers yet</Typography>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={() => setAddWorkerOpen(true)}
                    sx={{ color: '#1B5E20', borderColor: '#1B5E20' }}>
                    Add First Worker
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {workers.map(worker => (
                    <WorkerCard key={worker.id} worker={worker} token={token} onRefresh={load} />
                  ))}
                </Box>
              )
            ) : (
              /* Individual — show cases */
              <Box>
                {/* Client's own cases */}
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase' }}>
                  Client Cases
                </Typography>
                {cases.filter(c => !c.family_member_id || c.family_member_id === null).length === 0 ? (
                  <Typography sx={{ color: '#bbb', fontSize: '0.8rem', mb: 2 }}>No cases yet</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                    {cases.filter(c => !c.family_member_id || c.family_member_id === null).map(c => <CaseRow key={c.id} c={c} token={token} onRefresh={load} />)}
                  </Box>
                )}

                {/* Family members */}
                {members.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase' }}>
                      Family Members
                    </Typography>
                    {members.map(m => (
                      <Box key={m.id} sx={{ mb: 1.5, pl: 1.5, borderLeft: '3px solid #E8F5E9' }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          👤 {m.full_name} <span style={{ color: '#888', fontWeight: 400, fontSize: '0.75rem' }}>({m.relationship_type})</span>
                        </Typography>
                        {cases.filter(c => String(c.family_member_id) === String(m.id)).map(c => (
                          <CaseRow key={c.id} c={c} token={token} onRefresh={load} />
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      {/* Add Worker Dialog (Company) */}
      <Dialog open={addWorkerOpen} onClose={() => setAddWorkerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>👤 Add Worker</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Worker Full Name" value={workerName}
            onChange={e => setWorkerName(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Matter / Contract Number (optional)" value={workerMatterNum}
            onChange={e => setWorkerMatterNum(e.target.value)}
            helperText="e.g. 2026-001, ABC-PERM-01" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddWorkerOpen(false)}>Cancel</Button>
          <Button onClick={handleAddWorker} variant="contained" disabled={workerLoading || !workerName.trim()}
            sx={{ background: '#1B5E20' }}>
            {workerLoading ? <CircularProgress size={20} /> : 'Add Worker'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Family Member Dialog */}
      <Dialog open={addMemberOpen} onClose={() => setAddMemberOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>👥 Add Family Member</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Full Name" value={memberName}
            onChange={e => setMemberName(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <FormControl fullWidth>
            <InputLabel>Relationship</InputLabel>
            <Select value={memberRelation} label="Relationship"
              onChange={e => setMemberRelation(e.target.value)}>
              <MenuItem value="Spouse">Spouse</MenuItem>
              <MenuItem value="Child">Child</MenuItem>
              <MenuItem value="Parent">Parent</MenuItem>
              <MenuItem value="Sibling">Sibling</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberOpen(false)}>Cancel</Button>
          <Button onClick={handleAddMember} variant="contained" disabled={memberLoading || !memberName.trim()}
            sx={{ background: '#1B5E20' }}>
            {memberLoading ? <CircularProgress size={20} /> : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Case Dialog (Individual) */}
      <Dialog open={addCaseOpen} onClose={() => { setAddCaseOpen(false); setCaseError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Add Tracked Item</DialogTitle>
        <DialogContent>
          {caseError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{caseError}</Alert>}
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Case Type</InputLabel>
            <Select value={caseType} label="Case Type" onChange={e => setCaseType(e.target.value)}>
              <MenuItem value="eoir">⚖️ EOIR — Immigration Court (A-Number)</MenuItem>
              <MenuItem value="uscis">📋 USCIS — Receipt Number</MenuItem>
            </Select>
          </FormControl>
          {caseType === 'uscis' && (
            <TextField fullWidth label="Receipt Number (e.g. EAC9999103403)"
              value={receiptNumber} onChange={e => setReceiptNumber(e.target.value)}
              sx={{ mb: 2 }} inputProps={{ maxLength: 13 }} />
          )}
          {caseType === 'eoir' && (
            <>
              <TextField fullWidth label="A-Number (9 digits, e.g. 246725957)"
                value={alienNumber} onChange={e => setAlienNumber(e.target.value.replace(/\D/g, '').slice(0,9))}
                sx={{ mb: 2 }} />
              <Autocomplete options={NATIONALITY_OPTIONS} getOptionLabel={o => o.label}
                value={selectedNat} onChange={(e, v) => setSelectedNat(v)}
                renderInput={params => <TextField {...params} label="Nationality (as in EOIR system)" />}
                sx={{ mb: 2 }} />
            </>
          )}
          {client && client.client_type === 'individual' && members && members.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>For family member (optional)</InputLabel>
              <Select value={selectedMemberId} label="For family member (optional)"
                onChange={e => setSelectedMemberId(e.target.value)}>
                <MenuItem value="">— Client themselves —</MenuItem>
                {members.map(m => (
                  <MenuItem key={m.id} value={m.id}>{m.full_name} ({m.relationship_type})</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField fullWidth label="Description (optional)" value={caseDesc}
            onChange={e => setCaseDesc(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddCaseOpen(false); setCaseError(''); }}>Cancel</Button>
          <Button onClick={handleAddCase} variant="contained" disabled={caseLoading}
            sx={{ background: '#1B5E20' }}>
            {caseLoading ? <CircularProgress size={20} /> : 'Add Case'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}


// ─────────────────────────────────────
// Worker Card (for Company clients)
// ─────────────────────────────────────
function WorkerCard({ worker, token, onRefresh }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addCaseOpen, setAddCaseOpen] = useState(false);
  const [caseType, setCaseType] = useState('dol');
  const [dolNumber, setDolNumber] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [caseDesc, setCaseDesc] = useState('');
  const [caseLoading, setCaseLoading] = useState(false);
  const [caseError, setCaseError] = useState('');

  useEffect(() => { loadCases(); }, []);

  const loadCases = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/pro/matters/${worker.matter_id}/cases?token=${token}`);
      setCases((res.data || []).filter(c => c.pro_beneficiary_id === worker.id));
    } catch(e) {}
    finally { setLoading(false); }
  };

  const handleAddMember = async () => {
    if (!memberName.trim()) return;
    setMemberLoading(true);
    try {
      await axios.post(`${API_URL}/pro/clients/${client.id}/members?token=${token}&full_name=${encodeURIComponent(memberName)}&relationship_type=${encodeURIComponent(memberRelation)}`);
      setAddMemberOpen(false);
      setMemberName(''); setMemberRelation('Spouse');
      load();
    } catch(e) {}
    finally { setMemberLoading(false); }
  };

  const handleAddCase = async () => {
    setCaseError('');
    if (caseType === 'dol' && !dolNumber.trim()) { setCaseError('DOL case number required'); return; }
    if (caseType === 'uscis' && !receiptNumber.trim()) { setCaseError('Receipt number required'); return; }
    setCaseLoading(true);
    try {
      await axios.post(`${API_URL}/cases?token=${token}`, {
        case_type: caseType,
        description: caseDesc || null,
        pro_matter_id: worker.matter_id,
        pro_beneficiary_id: worker.id,
        ...(caseType === 'dol' ? { dol_case_number: dolNumber.trim() } :
           { receipt_number: receiptNumber.trim().toUpperCase() })
      });
      setAddCaseOpen(false);
      setDolNumber(''); setReceiptNumber(''); setCaseDesc('');
      loadCases();
    } catch(e) {
      setCaseError(e.response?.data?.detail || 'Failed to add case');
    } finally { setCaseLoading(false); }
  };

  return (
    <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 1.5, p: 2, background: '#FAFAFA' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>👤 {worker.full_name}</Typography>
          {worker.matter_number && (
            <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>
              Matter: {worker.matter_number}
            </Typography>
          )}
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAddCaseOpen(true)}
          sx={{ color: '#1B5E20', fontSize: '0.72rem' }}>
          Add Case
        </Button>
      </Box>

      {/* Cases for this worker */}
      {loading ? <CircularProgress size={16} sx={{ mt: 1 }} /> : (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {cases.map(c => <CaseRow key={c.id} c={c} token={token} onRefresh={loadCases} />)}
          {cases.length === 0 && (
            <Typography sx={{ fontSize: '0.75rem', color: '#bbb', mt: 0.5 }}>No cases yet</Typography>
          )}
        </Box>
      )}

      {/* Add Case Dialog */}
      <Dialog open={addCaseOpen} onClose={() => { setAddCaseOpen(false); setCaseError(''); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Add Case for {worker.full_name}</DialogTitle>
        <DialogContent>
          {caseError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{caseError}</Alert>}
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Case Type</InputLabel>
            <Select value={caseType} label="Case Type" onChange={e => setCaseType(e.target.value)}>
              <MenuItem value="dol">📄 DOL — Labor Certification (PERM, LCA, H-2A, H-2B)</MenuItem>
              <MenuItem value="uscis">📋 USCIS — Receipt Number</MenuItem>
            </Select>
          </FormControl>
          {caseType === 'dol' && (
            <TextField fullWidth label="DOL Case Number (e.g. G-100-12345-123456)"
              value={dolNumber} onChange={e => setDolNumber(e.target.value)}
              sx={{ mb: 2 }} helperText="From flag.dol.gov" />
          )}
          {caseType === 'uscis' && (
            <TextField fullWidth label="Receipt Number (e.g. EAC9999103403)"
              value={receiptNumber} onChange={e => setReceiptNumber(e.target.value)}
              sx={{ mb: 2 }} inputProps={{ maxLength: 13 }} />
          )}
          <TextField fullWidth label="Description (optional)" value={caseDesc}
            onChange={e => setCaseDesc(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddCaseOpen(false); setCaseError(''); }}>Cancel</Button>
          <Button onClick={handleAddCase} variant="contained" disabled={caseLoading}
            sx={{ background: '#1B5E20' }}>
            {caseLoading ? <CircularProgress size={20} /> : 'Add Case'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


// ─────────────────────────────────────
// Case Row — with detail modal, refresh, history
// ─────────────────────────────────────
function CaseRow({ c, token, onRefresh }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const typeColors = {
    eoir: { bg: '#E8D5F5', color: '#6A1B9A' },
    uscis: { bg: '#DCEEFA', color: '#0D47A1' },
    dol: { bg: '#FFF3E0', color: '#E65100' }
  };
  const colors = typeColors[c.case_type] || typeColors.uscis;

  const caseNum = c.case_type === 'uscis' ? c.receipt_number :
                  c.case_type === 'dol' ? c.dol_case_number :
                  `A-${c.eoir_alien_number}`;

  const statusText = c.case_type === 'uscis' ? c.current_status :
                     c.case_type === 'dol' ? c.dol_case_status :
                     c.eoir_pending_at_bia
                       ? (c.eoir_decision ? c.eoir_decision + ' — Pending at BIA' : 'Pending at BIA')
                       : c.eoir_next_hearing_date
                       ? `Hearing: ${new Date(c.eoir_next_hearing_date).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'})}`
                       : c.eoir_decision || null;

  const statusColor = c.case_type === 'dol'
    ? (c.dol_case_status?.includes('CERTIFIED') && !c.dol_case_status?.includes('EXPIRED') ? '#2E7D32' :
       c.dol_case_status?.includes('DENIED') ? '#C62828' : '#E65100')
    : '#555';

  const fmtDate = (d) => {
    if (!d) return null;
    try {
      const date = new Date(d);
      if (isNaN(date)) return d;
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch { return d; }
  };

  const formatAddress = (addr) => {
    if (!addr) return null;
    return addr.split('|').filter(Boolean).join(', ');
  };

  const handleCheck = async () => {
    setChecking(true);
    setCheckMsg('');
    try {
      if (c.case_type === 'eoir') {
        await axios.post(`${API_URL}/eoir-queue/add?case_id=${c.id}&token=${token}`);
        setCheckMsg('✅ Added to queue — Chrome extension will check shortly');
      } else {
        await checkCase(c.id);
        setCheckMsg('✅ Status updated');
        if (onRefresh) onRefresh();
      }
    } catch(e) {
      setCheckMsg('❌ Failed to check. Try again.');
    } finally {
      setChecking(false);
      setTimeout(() => setCheckMsg(''), 4000);
    }
  };

  const handleHistory = async () => {
    try {
      const res = await getCaseHistory(c.id);
      setHistoryData(res.data?.checks || res.data || []);
      setHistoryOpen(true);
    } catch(e) {
      setHistoryData([]);
      setHistoryOpen(true);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteCase(c.id);
      setDeleteConfirmOpen(false);
      if (onRefresh) onRefresh();
    } catch(e) {
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const decisionColor = c.eoir_decision_code === 'R' ? '#2E7D32'
    : c.eoir_decision_code === 'D' ? '#C62828'
    : c.eoir_decision_code === 'V' ? '#1565C0'
    : '#E65100';

  return (
    <>
      {/* ── Summary Row ── */}
      <Box sx={{ p: 1.5, background: 'white', borderRadius: 1, border: '1px solid #EEE', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

          {/* Left: case info — click opens detail modal */}
          <Box sx={{ flex: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            onClick={() => setDetailOpen(true)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={c.case_type.toUpperCase()} size="small"
                sx={{ fontSize: '0.65rem', fontWeight: 700, background: colors.bg, color: colors.color }} />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{caseNum}</Typography>
              {c.eoir_alien_name && (
                <Typography sx={{ fontSize: '0.75rem', color: '#555' }}>{c.eoir_alien_name}</Typography>
              )}
              {c.dol_employer_name && (
                <Typography sx={{ fontSize: '0.75rem', color: '#555' }}>🏢 {c.dol_employer_name}</Typography>
              )}
            </Box>
            {statusText ? (
              <Typography sx={{ fontSize: '0.75rem', color: statusColor, mt: 0.3 }}>{statusText}</Typography>
            ) : (
              <Typography sx={{ fontSize: '0.72rem', color: '#bbb', mt: 0.3 }}>⏳ Not checked yet</Typography>
            )}
            {c.eoir_judge_name && (
              <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>Judge: {c.eoir_judge_name}</Typography>
            )}
            {c.eoir_clock_status && (
              <Typography sx={{ fontSize: '0.72rem', color: c.eoir_clock_status === 'X' ? '#C62828' : '#2E7D32' }}>
                Clock: {c.eoir_clock_status === 'X' ? '⏹ Stopped' : '▶ Running'} {c.eoir_elapsed_days && `(${c.eoir_elapsed_days} days)`}
              </Typography>
            )}
          </Box>

          {/* Right: action buttons */}
          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
            {c.case_type !== 'dol' && (
              <IconButton size="small" title="Check Status" onClick={handleCheck}
                disabled={checking} sx={{ color: '#2E7D32' }}>
                {checking ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            )}
            <IconButton size="small" title="View History" onClick={handleHistory}
              sx={{ color: '#388E3C' }}>
              <HistoryIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" title="Delete Case" onClick={() => setDeleteConfirmOpen(true)}
              sx={{ color: '#C62828' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {checkMsg && (
          <Typography sx={{ fontSize: '0.72rem', color: checkMsg.startsWith('✅') ? '#2E7D32' : '#C62828', mt: 0.5 }}>
            {checkMsg}
          </Typography>
        )}
      </Box>

      {/* ── Detail Modal ── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={c.case_type.toUpperCase()} size="small"
            sx={{ background: colors.bg, color: colors.color, fontWeight: 700 }} />
          {caseNum}
        </DialogTitle>
        <DialogContent dividers>

          {/* ── USCIS detail ── */}
          {c.case_type === 'uscis' && (
            <Box>
              {c.form_type && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Form: {c.form_type}{c.submitted_date && ` • Submitted: ${c.submitted_date}`}
                </Typography>
              )}
              {c.description && c.description !== 'string' && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{c.description}</Typography>
              )}
              {c.current_status ? (
                <Chip label={c.current_status} color="success" size="small" sx={{ mb: 1 }} />
              ) : (
                <Chip label="Not checked yet" color="default" size="small" sx={{ mb: 1 }} />
              )}
              {c.checks?.[0]?.details && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.8rem' }}>
                  {c.checks[0].details}
                </Typography>
              )}
              {c.checks?.[0]?.checked_at && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  ✅ Updated: {new Date(c.checks[0].checked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>
              )}
            </Box>
          )}

          {/* ── EOIR detail ── */}
          {c.case_type === 'eoir' && (
            <Box>
              {c.eoir_alien_name && (
                <Typography variant="body1" fontWeight="500" sx={{ mb: 0.5 }}>{c.eoir_alien_name}</Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Nationality: {c.eoir_nationality_label || c.eoir_nationality_code}
                {c.eoir_docket_date && ` • Docket Date: ${fmtDate(c.eoir_docket_date)}`}
              </Typography>

              {c.eoir_alien_name && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>

                  {/* Next Hearing */}
                  <Box sx={{ background: '#F8F9FA', borderRadius: 1.5, p: 1.5 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>📅 Next Hearing</Typography>
                    {c.eoir_next_hearing_date ? (
                      <>
                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                          {fmtDate(c.eoir_next_hearing_date)}{c.eoir_next_hearing_time && ` at ${c.eoir_next_hearing_time}`}
                        </Typography>
                        {c.eoir_hearing_type && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {c.eoir_hearing_type === 'I' ? 'Individual Hearing' : c.eoir_hearing_type === 'M' ? 'Master Hearing' : c.eoir_hearing_type}
                          </Typography>
                        )}
                        {c.eoir_judge_name && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Judge: {c.eoir_judge_name}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        No future hearings scheduled
                      </Typography>
                    )}
                  </Box>

                  {/* Court Decision */}
                  <Box sx={{ background: '#F8F9FA', borderRadius: 1.5, p: 1.5 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>⚖️ Court Decision</Typography>
                    {c.eoir_decision ? (
                      <>
                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5, color: decisionColor }}>
                          {c.eoir_decision}
                        </Typography>
                        {c.eoir_decision_date && (
                          <Typography variant="caption" color="text.secondary">
                            Decision Date: {fmtDate(c.eoir_decision_date)}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        No decision yet
                      </Typography>
                    )}
                    {c.eoir_court_address && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        📍 {formatAddress(c.eoir_court_address)}
                      </Typography>
                    )}
                  </Box>

                  {/* BIA Appeal */}
                  {(c.eoir_appeal_filed || c.eoir_pending_at_bia) && (
                    <Box sx={{ background: '#FFF8E1', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>📋 BIA Appeal</Typography>
                      {c.eoir_appeal_due_date && (
                        <Box sx={{ mt: 0.5, p: 1, background: '#FFEBEE', borderRadius: 1, border: '1px solid #EF9A9A' }}>
                          <Typography variant="body2" color="error" fontWeight="bold">
                            ⚠️ Appeal deadline: {fmtDate(c.eoir_appeal_due_date)}
                          </Typography>
                        </Box>
                      )}
                      {c.eoir_appeal_filed_date && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>Filed: {fmtDate(c.eoir_appeal_filed_date)}</Typography>
                      )}
                      {c.eoir_pending_at_bia && (
                        <Chip label="Pending at BIA" color="warning" size="small" sx={{ mt: 0.5 }} />
                      )}
                      {c.eoir_bia_decision && (
                        <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5 }}>
                          BIA Decision: {c.eoir_bia_decision}{c.eoir_bia_decision_date && ` (${fmtDate(c.eoir_bia_decision_date)})`}
                        </Typography>
                      )}
                      {c.eoir_pending_at_bia && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Alien brief: {c.eoir_alien_brief_due ? `due ${fmtDate(c.eoir_alien_brief_due)}` : 'No due date at this time'}
                        </Typography>
                      )}
                      {c.eoir_pending_at_bia && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          DHS brief: {c.eoir_dhs_brief_due ? `due ${fmtDate(c.eoir_dhs_brief_due)}` : 'No due date at this time'}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Asylum Clock */}
                  {c.eoir_clock_status && (
                    <Box sx={{ background: '#F3F4F6', borderRadius: 1.5, p: 1.5 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>⏱ Asylum Clock</Typography>
                      <Typography variant="body2" fontWeight="600" sx={{ mt: 0.5,
                        color: c.eoir_clock_status === 'X' ? '#C62828' : '#2E7D32' }}>
                        {c.eoir_clock_status === 'X' ? 'Stopped' : c.eoir_clock_status === 'R' ? 'Running' : c.eoir_clock_status}
                      </Typography>
                      {c.eoir_elapsed_days && c.eoir_elapsed_days !== '0000' && (
                        <Typography variant="caption" color="text.secondary">{c.eoir_elapsed_days} days elapsed</Typography>
                      )}
                    </Box>
                  )}

                </Box>
              )}
            </Box>
          )}

          {/* ── DOL detail ── */}
          {c.case_type === 'dol' && (
            <Box>
              {c.dol_employer_name && (
                <Typography variant="body1" fontWeight="500" sx={{ mb: 0.5 }}>🏢 {c.dol_employer_name}</Typography>
              )}
              {c.dol_job_title && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>💼 {c.dol_job_title}</Typography>
              )}
              {c.dol_case_status && (
                <Chip label={c.dol_case_status} size="small" sx={{ mb: 1,
                  background: c.dol_case_status.includes('CERTIFIED') && !c.dol_case_status.includes('EXPIRED') ? '#E8F5E9' : '#FFF3E0',
                  color: c.dol_case_status.includes('CERTIFIED') && !c.dol_case_status.includes('EXPIRED') ? '#2E7D32' : '#E65100',
                  fontWeight: 700 }} />
              )}
              {c.dol_submitted_date && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Submitted: {fmtDate(c.dol_submitted_date)}
                </Typography>
              )}
              {c.dol_decision_date && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Decision: {fmtDate(c.dol_decision_date)}
                </Typography>
              )}
              {c.description && c.description !== 'string' && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{c.description}</Typography>
              )}
            </Box>
          )}

        </DialogContent>
        <DialogActions>
          {c.case_type !== 'dol' && (
            <Button onClick={handleCheck} disabled={checking}
              startIcon={checking ? <CircularProgress size={16} /> : <RefreshIcon />}
              sx={{ color: '#2E7D32' }}>
              {checking ? 'Checking...' : 'Check Status'}
            </Button>
          )}
          <Button onClick={handleHistory} startIcon={<HistoryIcon />} sx={{ color: '#388E3C' }}>
            History
          </Button>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#C62828', fontWeight: 'bold' }}>⚠️ Delete Case</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            You are about to permanently delete <strong>{caseNum}</strong> and all its history.
          </Alert>
          <Typography sx={{ fontSize: '0.85rem', color: '#C62828', fontWeight: 600 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── History Dialog ── */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>📋 Status History — {caseNum}</DialogTitle>
        <DialogContent dividers>
          {historyData.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No history yet</Typography>
          ) : (
            historyData.map((h, i) => (
              <Box key={i} sx={{ mb: 2, pb: 2, borderBottom: i < historyData.length - 1 ? '1px solid #EEE' : 'none' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Chip label={h.status} size="small" color="default" />
                  <Typography variant="caption" color="text.secondary">
                    {h.checked_at ? new Date(h.checked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                  </Typography>
                </Box>
                {h.details && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', mt: 0.5 }}>
                    {h.details}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}